import type { Auth } from 'firebase/auth';
import { type Firestore, collection, doc, getDoc, getDocs, limit, query, setDoc, where } from 'firebase/firestore';
import { type FirebaseStorage, getDownloadURL, ref, uploadBytes } from 'firebase/storage';

import { categoryFor } from '../../features/documents/application/documentChecklist';
import type {
  DocumentKind,
  DocumentPort,
  UploadedDocument,
} from '../../features/documents/application/ports/documentPort';
import {
  ALLOWED_MIME_TYPES,
  DocumentError,
  MAX_UPLOAD_BYTES,
} from '../../features/documents/application/ports/documentPort';

/**
 * Cloud Storage implementation of `DocumentPort` (blueprint §11.1).
 *
 * The storage path carries the owner's auth uid and the category, so ownership
 * and readability are structural rather than metadata a client could lie about
 * — the storage rules match on the same path.
 */

/** Bounded read: the checklist cannot require more than a handful. */
const MAX_DOCUMENTS_READ = 60;

/**
 * `path` is named in a refusal on purpose.
 *
 * "You do not have permission" was true and useless: the rules refuse by path,
 * so the path is the one fact that identifies which refusal happened. A stale
 * bundle uploading to the old shape now says so out loud instead of looking
 * like a permissions problem on the account.
 */
function translate(error: unknown, path?: string): DocumentError {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';

  if (code.includes('unauthorized') || code.includes('permission-denied')) {
    return new DocumentError(
      'forbidden',
      path === undefined
        ? 'لا تملك صلاحية رفع هذا الملف.'
        : `رُفض رفع الملف إلى المسار: ${path}`,
    );
  }
  if (code.includes('retry-limit') || code.includes('unavailable')) {
    return new DocumentError('network', 'تعذّر إتمام الرفع. تحقق من الإنترنت وحاول مجددًا.');
  }
  return new DocumentError('unknown', 'تعذّر رفع الملف. حاول مرة أخرى.');
}

export function createFirebaseDocumentAdapter(
  firestore: Firestore,
  storage: FirebaseStorage,
  auth: Auth,
): DocumentPort {
  /**
   * Both identifiers, because the two stores are keyed differently.
   *
   * Firestore rows are keyed by employee id — that is what the committee reads
   * and what the roster knows. The storage path is keyed by the auth uid,
   * because Cloud Storage rules cannot read Firestore: the only owner identity
   * available to them is `request.auth.uid`, so the path must carry that or
   * ownership cannot be checked at all.
   *
   * Returning both from one place is deliberate. They were confused for each
   * other once already, and every upload was rejected.
   */
  async function resolveMyIdentity(): Promise<{ uid: string; employeeId: string }> {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new DocumentError('forbidden', 'يجب تسجيل الدخول أولًا.');

    const link = await getDoc(doc(firestore, 'userAccounts', uid));
    const employeeId: unknown = link.data()?.['employeeId'];

    if (typeof employeeId !== 'string') {
      throw new DocumentError('forbidden', 'لا يوجد سجل موظف مرتبط بحسابك.');
    }
    return { uid, employeeId };
  }

  /**
   * Re-checks size and type here as well as in the UI.
   *
   * The screen checks so the employee is told early; this checks because a
   * screen is not a boundary. The storage rules check a third time, and that
   * one is the boundary.
   */
  function assertAcceptable(file: File): void {
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new DocumentError('too_large', 'حجم الملف أكبر من الحد المسموح.');
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      throw new DocumentError('wrong_type', 'صيغة الملف غير مقبولة.');
    }
  }

  async function store(
    identity: { uid: string; employeeId: string },
    documentId: string,
    file: File,
    kind: DocumentKind,
    dependentId: string | null,
  ): Promise<void> {
    const { uid, employeeId } = identity;
    const category = categoryFor(kind);

    // The path is the authorization. `employees/{uid}/medical/...` is readable
    // by HR and the manager but never by finance, and the storage rules say so
    // by matching this exact shape — including that first segment being the
    // uid the rules compare against.
    const path = `employees/${uid}/${category}/${documentId}`;
    const storageRef = ref(storage, path);

    try {
      await uploadBytes(storageRef, file, { contentType: file.type });
    } catch (error) {
      throw translate(error, path);
    }

    /*
     * The download URL is deliberately not stored.
     *
     * It carries its own access token, so the string is the permission. Kept
     * in a document the whole committee reads, it would have handed finance a
     * working link to every medical report the storage rules deny them. It is
     * fetched per viewing instead — see `getViewUrl`.
     */
    await setDoc(doc(firestore, 'employeeDocuments', documentId), {
      schemaVersion: 1,
      id: documentId,
      employeeId,
      kind,
      category,
      fileName: file.name,
      sizeBytes: file.size,
      contentType: file.type,
      dependentId,
      uploadedAt: new Date().toISOString(),
      updatedBy: employeeId,
    });
  }

  return {
    async listMyDocuments(): Promise<readonly UploadedDocument[]> {
      try {
        const { employeeId } = await resolveMyIdentity();

        const snapshot = await getDocs(
          query(
            collection(firestore, 'employeeDocuments'),
            where('employeeId', '==', employeeId),
            limit(MAX_DOCUMENTS_READ),
          ),
        );

        return snapshot.docs.map((entry) => {
          const data = entry.data();
          const size: unknown = data['sizeBytes'];
          const dependentId: unknown = data['dependentId'];

          return {
            id: entry.id,
            kind: String(data['kind']) as DocumentKind,
            category: data['category'] === 'medical' ? 'medical' : 'identity',
            fileName: String(data['fileName'] ?? ''),
            sizeBytes: typeof size === 'number' ? size : 0,
            uploadedAt: String(data['uploadedAt'] ?? ''),
            dependentId: typeof dependentId === 'string' ? dependentId : null,
          };
        });
      } catch (error) {
        throw error instanceof DocumentError ? error : translate(error);
      }
    },

    async upload(file, kind, dependentId): Promise<void> {
      try {
        assertAcceptable(file);
        const identity = await resolveMyIdentity();
        const { employeeId } = identity;

        /*
         * Deterministic id from owner + kind + dependant.
         *
         * Re-uploading the same requirement replaces it rather than piling up
         * duplicates, which is what an employee retrying a bad scan actually
         * means — and it keeps the checklist from matching an old file.
         */
        const documentId = `${employeeId}__${kind}__${dependentId ?? 'self'}`;

        await store(identity, documentId, file, kind, dependentId ?? null);
      } catch (error) {
        throw error instanceof DocumentError ? error : translate(error);
      }
    },

    async getViewUrl(documentId: string): Promise<string> {
      try {
        const { uid } = await resolveMyIdentity();

        // Rebuilt from the id rather than read from the row: the row is not
        // where authority lives, and a stored link would be usable by anyone
        // who could read the row.
        const existing = await getDoc(doc(firestore, 'employeeDocuments', documentId));
        if (!existing.exists()) {
          throw new DocumentError('unknown', 'لم يُعثر على الوثيقة.');
        }

        const kind = String(existing.data()['kind']) as DocumentKind;
        const path = `employees/${uid}/${categoryFor(kind)}/${documentId}`;

        // Subject to the storage rules: this is the check, not a formality.
        return await getDownloadURL(ref(storage, path));
      } catch (error) {
        throw error instanceof DocumentError ? error : translate(error);
      }
    },

    async replace(documentId, file): Promise<void> {
      try {
        assertAcceptable(file);
        const identity = await resolveMyIdentity();

        const existing = await getDoc(doc(firestore, 'employeeDocuments', documentId));
        if (!existing.exists()) {
          throw new DocumentError('unknown', 'لم يُعثر على الملف المطلوب استبداله.');
        }

        const data = existing.data();
        const kind = String(data['kind']) as DocumentKind;
        const dependentId: unknown = data['dependentId'];

        await store(
          identity,
          documentId,
          file,
          kind,
          typeof dependentId === 'string' ? dependentId : null,
        );
      } catch (error) {
        throw error instanceof DocumentError ? error : translate(error);
      }
    },
  };
}
